import { useEffect, useRef, useState } from 'react'

import { colors, size } from '@styles/theme'
import useDialog from '@hooks/useDialog'
import styled from '@emotion/styled'

import { PiCaretRightBold, PiPaperPlaneRightBold, PiSpinnerBold } from 'react-icons/pi'
import { Dialog, IconButton, Input, InputAdornment } from '@mui/material'
import { UserMediaDrawer } from './user_media_upload/UserMediaDrawer'
import { TbMoodSmile, TbPhotoPlus, } from 'react-icons/tb'
import Picker from '@emoji-mart/react'

const InputBar = styled.form`
  display: flex;
  align-items: center;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: ${size(0.65)} ${size(0.5)};
  background-color: black;
`

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  flex-grow: 1;
  padding: ${size(0.1)} ${size(2)} ${size(0.1)} ${size(2.5)};
  border-radius: 25px;
  background-color: #333333;
  // background-color: #212029;
  // background-color: #31303d;
`

const MessageInput = styled(Input)`
  flex-grow: 1;
  width: 100%;
  min-height: 2.75rem;
  padding-left: 0;
  color: white;
  border: none;
`

const LoadText = styled.div`
  position: absolute;
  top: 0;
  right: ${size(1)};
  transform: translateY(-100%);

  display: flex;
  align-items: center;
  gap: ${size(0.25)};

  color: grey;
  font-size: ${size(1.25)};
`


const MessagesInputBar = ({ handleSubmit, disabled, loadText, handleConversationNewMessageSent, addNewMessageAfterSend }) => {
  const [isMessageEmojiDialogOpen, openMessageEmojiDialog, closeMessageEmojiDialog] = useDialog(`messages_emoji_dialog`, 'messages-emoji-dialog-open')
  const [isUserMediaOpen, openUserMedia, closeUserMedia] = useDialog(`user-media_bottom-drawer`, 'user-media_bottom-drawer-open')
  const [isMediaButtonHidden, setIsMediaButtonHidden] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const inputRef = useRef(null)
  const barRef = useRef(null)

  // Resets component state on unmount
  useEffect(() => {
    return () => {
      setIsMediaButtonHidden(false)
      setNewMessage('')
    }
  }, [])

  const internalHandleSubmit = (e) => {
    e.preventDefault()
    if (disabled) return;
    // NOTE: We have some issues with the keyboard unfocusing and disappearing, and either of these functions
    //       can help stop that, though most of the "disappearing" prevention is done through using the touchEnd
    //       event rather than the click event. And for some reason the form submit event is affected by that as well.
    // inputRef.current?.click()
    // inputRef.current?.focus()
    // At least as of 6/25/2024, there doesn't seem to be issues with the keyboard disappearing???? -__- I don't really know what's going on here.

    // Call submission from parent component
    if (typeof handleSubmit === 'function') {
      const trimmed = newMessage.trim()
      if (trimmed) handleSubmit(trimmed)
    } else {
      console.error('MessageInputBar: handleSubmit is not a function')
    }

    // Wipe out old state so the user can start writing a new one
    setNewMessage('')
  }

  const renderLoadText = () => {
    if (loadText) return (
      <LoadText>
        <PiSpinnerBold
          size={size(1.25)}
          className='animation_rotation'
        />
        {loadText}
      </LoadText>
    )
  }

  const renderLeftActions = () => {
    if (isMediaButtonHidden) return (
      <IconButton
        style={{
          padding: '0 0.5rem 0.9rem 0.25rem',
          alignSelf: 'flex-end'
        }}
        onClick={() => {
          setIsMediaButtonHidden(false)
          inputRef?.current?.focus()
        }}>
        <PiCaretRightBold fill='white' size={size(2.25)} />
      </IconButton>
    ); else return (
      <IconButton
        onClick={openUserMedia}
        style={{
          marginRight: size(1),
          marginLeft: size(0.5),
          alignSelf: 'flex-end'
        }}
        role={undefined}
        component='label'
        aria-label='upload media'>
        <TbPhotoPlus
          style={{ color: colors.statusColors.right_now('light') }}
          size={size(3.35)}
        />
      </IconButton>
    )
  }

  return (
    <InputBar
      ref={barRef}
      onSubmit={internalHandleSubmit}>
      <UserMediaDrawer
        isOpen={isUserMediaOpen}
        setClosed={closeUserMedia}
        addNewMessageAfterSend={addNewMessageAfterSend}
        handleConversationNewMessageSent={handleConversationNewMessageSent}
      />
      {renderLoadText()}
      {renderLeftActions()}
      <InputContainer>
        <MessageInput
          inputRef={inputRef}
          value={newMessage}
          name='newMessage'
          multiline
          maxRows={5}
          disableUnderline
          onChange={(e) => {
            setNewMessage(e.target.value)
            if (e.target.value?.length > 0) {
              setIsMediaButtonHidden(true)
            } else {
              setIsMediaButtonHidden(false)
            }
          }}
          placeholder={'What\'s on your mind?'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                style={{ height: size(6), width: size(6) }}
                onClick={openMessageEmojiDialog}
                // aria-label="submit message"
                edge="end">
                <TbMoodSmile
                  color={colors.statusColors.right_now('light')}
                  size={size(3.5)}
                />
              </IconButton>
              <IconButton
                style={{ height: size(6), width: size(6) }}
                type='submit'
                onTouchEnd={internalHandleSubmit}
                aria-label="submit message"
                edge="end">
                <PiPaperPlaneRightBold
                  fill={colors.statusColors.right_now('light')}
                  size={size(3)}
                />
              </IconButton>
            </InputAdornment>
          }
        />
      </InputContainer>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'transparent',
            margin: 0,
          }
        }}
        open={isMessageEmojiDialogOpen}
        onClose={closeMessageEmojiDialog}
      >
        <Picker data={
          async () => {
            const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data')
            return response.json()
          }}
          onEmojiSelect={e => {
            setNewMessage(newMessage + e.native)
            closeMessageEmojiDialog()
          }}
        />
      </Dialog>
    </InputBar>
  )
}

export default MessagesInputBar
